import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import { cachedGet } from "../lib/outbox.js";
import { api } from "../lib/api.js";
import { centsToDisplay, dollarsToCents, todayISO } from "../lib/money.js";
import { Field, inputCls } from "../components/ui.jsx";

// Chart colors: income = emerald, expenses = brand copper. Pair validated for
// colorblind separation + contrast (dataviz six-checks) — slate text-gray
// fails as a mark color, so expenses get copper here while staying slate in text.
const CHART = { income: "#059669", expense: "#B87333" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function MonthlyChart({ monthly }) {
  const max = Math.max(...monthly.flatMap((m) => [m.income_cents, m.expense_cents]));
  if (!max) return null;
  const W = 348,
    H = 124,
    plotH = H - 16,
    group = W / 12,
    barW = 8,
    gap = 2;
  const scale = (v) => (v / max) * (plotH - 8);
  return (
    <div className="mt-4">
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: CHART.income }} />
          Income
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: CHART.expense }} />
          Expenses
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-1 w-full" role="img" aria-label="Monthly income and expenses">
        <line x1="0" y1={plotH} x2={W} y2={plotH} stroke="#e2e8f0" strokeWidth="1" />
        {monthly.map((m, i) => {
          const x0 = i * group + (group - (barW * 2 + gap)) / 2;
          const hi = m.income_cents ? Math.max(2, scale(m.income_cents)) : 0;
          const he = m.expense_cents ? Math.max(2, scale(m.expense_cents)) : 0;
          return (
            <g key={m.month}>
              <title>{`${MONTHS[i]} — Income ${centsToDisplay(m.income_cents)} · Expenses ${centsToDisplay(m.expense_cents)}`}</title>
              {hi > 0 && <rect x={x0} y={plotH - hi} width={barW} height={hi} rx="2" fill={CHART.income} />}
              {he > 0 && <rect x={x0 + barW + gap} y={plotH - he} width={barW} height={he} rx="2" fill={CHART.expense} />}
              <text x={i * group + group / 2} y={H - 4} textAnchor="middle" fontSize="8.5" fill="#94a3b8">
                {MONTHS[i][0]}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ---------------- Tax set-aside (Phase 7) ---------------- */
function EstimatesCard() {
  const [est, setEst] = useState(null);
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [pay, setPay] = useState({ amount: "", jurisdiction: "federal", date: todayISO() });
  const [busy, setBusy] = useState(false);

  const load = () => api("/api/reports/estimates").then(setEst).catch(() => setEst(undefined));
  useEffect(() => {
    load();
  }, []);

  if (!est) return null;

  async function record(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/api/reports/estimates", {
        method: "POST",
        body: { action: "record", paid_date: pay.date, amount_cents: dollarsToCents(pay.amount), jurisdiction: pay.jurisdiction, period: est.next_due?.period },
      });
      setRecording(false);
      setPay({ amount: "", jurisdiction: "federal", date: todayISO() });
      load();
    } finally {
      setBusy(false);
    }
  }

  const t = est.tax;
  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Tax set-aside (estimate)</p>
          <p className="mt-1 text-2xl font-semibold text-navy-700">{centsToDisplay(est.remaining_cents)}</p>
          <p className="text-xs text-slate-500">
            still to set aside · send ~{centsToDisplay(est.suggested_next_cents)} by {est.next_due.due} ({est.next_due.period})
          </p>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-navy-700">
          {open ? "Hide" : "Details"}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-sm">
          {/* The actual math, top to bottom: income − deductions = taxable
              profit; taxes on that = the liability; minus paid = set-aside. */}
          <div className="flex justify-between">
            <span className="text-slate-600">Income so far</span>
            <span className="text-slate-700">{centsToDisplay(est.income_cents)}</span>
          </div>
          {[
            ["Business expenses", est.deductions.ledger_expenses_cents],
            [`Mileage (${est.deductions.mileage_miles} mi @ IRS rate)`, est.deductions.mileage_cents],
            ["Home office (simplified)", est.deductions.home_office_cents],
            ["Assets (straight-line est.)", est.deductions.assets_cents],
          ]
            .filter(([, c]) => c > 0)
            .map(([label, cents]) => (
              <div key={label} className="flex justify-between pl-3">
                <span className="text-slate-500">{label}</span>
                <span className="text-emerald-700">−{centsToDisplay(cents)}</span>
              </div>
            ))}
          <div className="flex justify-between border-t border-slate-100 pt-1">
            <span className="font-semibold text-slate-700">Taxable profit (after deductions)</span>
            <span className="font-semibold text-slate-700">{centsToDisplay(est.net_profit_cents)}</span>
          </div>

          <div className="pt-2" />
          {[
            ["Self-employment tax (15.3%)", t.se_cents],
            ["Federal income (rough)", t.federal_cents],
            ["Indiana state", t.state_cents],
            ["County", t.county_cents],
          ].map(([label, cents]) => (
            <div key={label} className="flex justify-between pl-3">
              <span className="text-slate-500">{label}</span>
              <span className="text-slate-600">{centsToDisplay(cents)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-slate-100 pt-1">
            <span className="font-semibold text-navy-700">Estimated tax liability</span>
            <span className="font-semibold text-navy-700">{centsToDisplay(t.total_cents)}</span>
          </div>
          <div className="flex justify-between pl-3">
            <span className="text-slate-500">Already paid</span>
            <span className="text-emerald-700">−{centsToDisplay(est.paid_cents)}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-semibold text-slate-700">Still to set aside</span>
            <span className="font-semibold text-slate-700">{centsToDisplay(est.remaining_cents)}</span>
          </div>
          {est.payments.length > 0 && (
            <div className="pt-1">
              {est.payments.map((p) => (
                <p key={p.id} className="text-xs text-slate-400">
                  Paid {centsToDisplay(p.amount_cents)} ({p.jurisdiction}) {p.paid_date}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {recording ? (
        <form onSubmit={record} className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          <div className="grid grid-cols-3 gap-2">
            <Field label="Amount ($)">
              <input value={pay.amount} onChange={(e) => setPay((s) => ({ ...s, amount: e.target.value }))} inputMode="decimal" className={inputCls} />
            </Field>
            <Field label="To">
              <select value={pay.jurisdiction} onChange={(e) => setPay((s) => ({ ...s, jurisdiction: e.target.value }))} className={inputCls}>
                <option value="federal">IRS</option>
                <option value="indiana">Indiana</option>
              </select>
            </Field>
            <Field label="Date">
              <input type="date" value={pay.date} onChange={(e) => setPay((s) => ({ ...s, date: e.target.value }))} className={inputCls} />
            </Field>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setRecording(false)} className="flex-1 rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-500">
              Cancel
            </button>
            <button disabled={busy} className="flex-1 rounded-xl bg-navy-700 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
              {busy ? "Saving…" : "✓ Record"}
            </button>
          </div>
        </form>
      ) : (
        <button onClick={() => setRecording(true)} className="mt-3 text-xs font-semibold text-navy-700">
          + Record a payment sent
        </button>
      )}

      <p className="mt-2 text-[11px] text-slate-400">
        {est.rates_verified_with_cpa ? "" : "⚠️ Rates are placeholders — review with your CPA. "}
        {est.county_rate_missing ? "County rate not set. " : ""}
        Not tax advice.
      </p>
    </section>
  );
}

/* ---------------- Smart to-do ---------------- */
// The balance is not the same as what's free to spend. Tax set-aside and tithe
// are already promised, expenses booked since the last import haven't been
// deducted yet, and business costs carried on a personal card never touched
// this account at all. This card does that arithmetic out loud.
function BalanceCard() {
  const [balances, setBalances] = useState(null);
  const [position, setPosition] = useState(null);
  const [taxReserveCents, setTaxReserve] = useState(0);
  const [titheOwedCents, setTitheOwed] = useState(0);
  useEffect(() => {
    api("/api/money/bank")
      .then((d) => setBalances(d.balances || []))
      .catch(() => setBalances([]));
    api("/api/reports/available")
      .then((d) => setPosition(d.position))
      .catch(() => {});
    api("/api/reports/estimates")
      .then((d) => setTaxReserve(Math.max(0, d?.remaining_cents || 0)))
      .catch(() => {});
    api("/api/tithe")
      .then((d) => setTitheOwed(Math.max(0, d?.ytd?.owed_cents || 0)))
      .catch(() => {});
  }, []);
  if (!balances?.length) return null;

  const primary = position;
  const safe = primary
    ? primary.balance_cents - primary.uncleared_cents - taxReserveCents - titheOwedCents
    : null;
  const Row = ({ label, cents, negative }) => (
    <div className="flex items-baseline justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className={negative ? "text-slate-600" : "text-slate-600"}>
        {negative ? "−" : ""}
        {centsToDisplay(cents)}
      </span>
    </div>
  );

  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm">
      <Link to="/money?tab=import" className="flex items-baseline justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Safe to spend</p>
          <p className={`text-2xl font-semibold ${safe !== null && safe < 0 ? "text-red-600" : "text-navy-700"}`}>
            {safe !== null ? centsToDisplay(safe) : centsToDisplay(balances[0].amount_cents)}
          </p>
        </div>
        <span className="text-slate-300">›</span>
      </Link>

      {primary && (
        <div className="mt-3 space-y-1 border-t border-slate-100 pt-2">
          <Row label={`${primary.account} balance · as of ${primary.as_of}`} cents={primary.balance_cents} />
          {primary.uncleared_cents > 0 && (
            <Row label={`Spent since, not yet on a statement (${primary.uncleared_count})`} cents={primary.uncleared_cents} negative />
          )}
          {taxReserveCents > 0 && <Row label="Tax set-aside still owed" cents={taxReserveCents} negative />}
          {titheOwedCents > 0 && <Row label="Tithe to give" cents={titheOwedCents} negative />}
        </div>
      )}

      {balances.length > 1 && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          {balances
            .filter((b) => b.account !== primary?.account)
            .map((b) => (
              <Row key={b.account} label={`${b.account} · as of ${b.as_of}`} cents={b.amount_cents} />
            ))}
        </div>
      )}

      {primary?.personally_covered_cents > 0 && (
        <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
          You've covered {centsToDisplay(primary.personally_covered_cents)} of business costs on a personal card
          ({primary.personally_covered_count} {primary.personally_covered_count === 1 ? "expense" : "expenses"}). Still
          deductible, and it never left this account — reimburse yourself with an owner draw whenever you like.
        </p>
      )}
    </section>
  );
}

function AttentionCard() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api("/api/reports/attention").then((d) => setItems(d.items)).catch(() => setItems([]));
  }, []);
  if (!items?.length) return null;
  return (
    <section className="rounded-2xl bg-white shadow-sm">
      <p className="px-4 pt-4 text-sm font-semibold text-slate-700">Needs attention</p>
      <div className="divide-y divide-slate-100">
        {items.map((i) => (
          <Link key={i.key} to={i.url} className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-slate-700">{i.label}</span>
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">{i.count}</span>
          </Link>
        ))}
      </div>
      <div className="h-2" />
    </section>
  );
}

// The money view — deliberately OFF the home screen so demoing the app never
// exposes financials. Estimates/tax widgets (Phase 7) will live here too.
export default function Finances() {
  const [pnl, setPnl] = useState(null);
  const [recent, setRecent] = useState(null);
  const [tithe, setTithe] = useState(null);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    cachedGet("/api/reports/pnl")
      .then(({ data, stale: s }) => {
        setPnl(data);
        if (s) setStale(true);
      })
      .catch(() => setPnl(undefined));
    cachedGet("/api/transactions?limit=8")
      .then(({ data, stale: s }) => {
        setRecent(data.transactions);
        if (s) setStale(true);
      })
      .catch(() => setRecent([]));
    cachedGet("/api/tithe")
      .then(({ data }) => setTithe(data))
      .catch(() => {});
  }, []);

  return (
    <Shell title="Dashboard" back>
      {stale && (
        <p className="rounded-xl bg-slate-100 px-4 py-2 text-center text-xs text-slate-500">
          Offline — showing last synced data
        </p>
      )}

      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            {pnl?.year || new Date().getFullYear()} so far
          </p>
          {pnl && (
            <p className="text-xs text-slate-400">
              Net{" "}
              <span className={`font-semibold ${pnl.totals.net_cents >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {centsToDisplay(pnl.totals.net_cents)}
              </span>
            </p>
          )}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-semibold text-emerald-600">
              {pnl ? centsToDisplay(pnl.totals.income_cents) : "—"}
            </p>
            <p className="text-xs text-slate-500">Income</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-slate-700">
              {pnl ? centsToDisplay(pnl.totals.expense_cents) : "—"}
            </p>
            <p className="text-xs text-slate-500">Expenses</p>
          </div>
        </div>
        {pnl?.monthly && <MonthlyChart monthly={pnl.monthly} />}
      </section>

      <BalanceCard />
      <AttentionCard />
      <EstimatesCard />

      {tithe?.ytd?.tithe_cents > 0 && (
        <Link to="/tithe" className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm">
          <div>
            <p className="text-sm font-semibold text-slate-700">🙏 Tithe</p>
            <p className="text-xs text-slate-400">
              {centsToDisplay(tithe.ytd.given_cents)} given this year
              {tithe.ytd.owed_cents > 0 ? ` · ${centsToDisplay(tithe.ytd.owed_cents)} to give` : " · all given ✓"}
            </p>
          </div>
          <span className="text-slate-300">›</span>
        </Link>
      )}

      <section className="rounded-2xl bg-white shadow-sm">
        <div className="flex items-center justify-between px-4 pt-4">
          <p className="text-sm font-semibold text-slate-700">Recent entries</p>
          <Link to="/ledger" className="text-xs font-semibold text-navy-700">
            See all →
          </Link>
        </div>
        {recent === null && <p className="px-4 py-6 text-center text-sm text-slate-400">Loading…</p>}
        {recent?.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-slate-400">Nothing yet — add your first income or expense.</p>
        )}
        <div className="divide-y divide-slate-100">
          {(recent || []).map((t) => (
            <Link key={t.id} to={`/edit/${t.id}`} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-800">{t.vendor || t.account_name}</p>
                <p className="text-xs text-slate-400">{t.date}</p>
              </div>
              <p
                className={`ml-3 shrink-0 text-sm font-semibold ${
                  t.type === "income" || t.type === "owner_contribution" ? "text-emerald-600" : "text-slate-800"
                }`}
              >
                {t.type === "income" || t.type === "owner_contribution" ? "+" : "−"}
                {centsToDisplay(t.amount_cents)}
              </p>
            </Link>
          ))}
        </div>
        <div className="h-2" />
      </section>

      <p className="text-center text-xs text-slate-400">
        Estimates shown in this app are not tax advice — confirm with your CPA.
      </p>
    </Shell>
  );
}
